import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { tokens } from '../../global/theme/tokens';
import { API_POST, DBRequest, useConfig } from '../../api/api';

// Undo send popups (apple mail style): while the configured send delay
// (config.mail.outboundEmailSendDelay) is running, every freshly sent email gets a
// small card in the corner of the mail client offering to cancel it again. A
// thin progress bar at the bottom indicates how much time is left until the
// email is actually sent.
export const ECUndoSend = ({ mails, onRemove }) => {
  return (
    <Box
      className="fixed bottom-4 right-4 flex flex-col gap-2 items-end"
      sx={{ zIndex: 1400 }}
    >
      {mails.map((mail) => (
        <UndoSendCard
          key={mail.id}
          mail={mail}
          onRemove={() => onRemove(mail.id)}
        />
      ))}
    </Box>
  );
};

const UndoSendCard = ({ mail, onRemove }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  // 'pending' | 'cancelling' | 'cancelled' | 'error'
  const [state, setState] = useState('pending');
  const [errorMessage, setErrorMessage] = useState(null);
  const [progress, setProgress] = useState(0);

  // the interval callback needs the current state without re-registering
  const stateRef = useRef(state);
  stateRef.current = state;

  // progress clock — fills up until the backend sends the email
  useEffect(() => {
    const tick = setInterval(() => {
      if (stateRef.current !== 'pending' && stateRef.current !== 'cancelling')
        return;
      const elapsed = Date.now() - (mail.expiresAt - mail.duration);
      const newProgress = Math.min((elapsed / mail.duration) * 100, 100);
      setProgress(newProgress);
      // time ran out without a cancel -> the email is on its way
      if (newProgress >= 100 && stateRef.current === 'pending') onRemove();
    }, 100);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mail.id]);

  // keep the result visible for a moment, then remove the card
  useEffect(() => {
    if (state !== 'cancelled' && state !== 'error') return;
    const timeout = setTimeout(onRemove, state === 'error' ? 5000 : 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function handleCancel() {
    if (stateRef.current !== 'pending') return;
    setState('cancelling');
    DBRequest({
      config,
      path: `outbound-emails/${mail.id}/cancel`,
      method: API_POST(),
      onResponse: () => setState('cancelled'),
      onError: (error) => {
        setErrorMessage(
          t(
            error?.message ||
              'Cannot cancel outgoing emails that have already been sent',
          ),
        );
        setState('error');
      },
    });
  }

  const stateMap = {
    pending: {
      title: t('Sending email'),
      Icon: ScheduleSendIcon,
      color: colors.info,
    },
    cancelling: {
      title: t('Sending email'),
      Icon: ScheduleSendIcon,
      color: colors.info,
    },
    cancelled: {
      title: t('Sending cancelled'),
      Icon: BlockIcon,
      color: colors.grey[500],
    },
    error: {
      title: t('Could not cancel email'),
      Icon: WarningAmberIcon,
      color: colors.error,
    },
  };
  const { title, Icon, color } = stateMap[state];
  const isPending = state === 'pending' || state === 'cancelling';

  return (
    <Box
      className="rounded-lg overflow-hidden backdrop-blur-lg shadow-lg w-[320px]"
      sx={{
        bgcolor: colors.glass,
        border: `1px solid ${colors.card}`,
      }}
    >
      <Box className="flex items-center gap-2 p-3">
        <Icon sx={{ color }} />
        <Box className="flex flex-col overflow-hidden w-full">
          <Typography fontSize="13px" fontWeight={600} noWrap>
            {title}
          </Typography>
          <Typography fontSize="12px" className="opacity-60" noWrap>
            {state === 'error'
              ? errorMessage
              : mail.subject || mail.to?.[0]?.email || ''}
          </Typography>
        </Box>
        {isPending && (
          <Button
            size="small"
            color="error"
            className="whitespace-nowrap"
            onClick={handleCancel}
            disabled={state === 'cancelling'}
          >
            {state === 'cancelling' ? (
              <CircularProgress size="16px" color="error" />
            ) : (
              t('Undo')
            )}
          </Button>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={isPending ? progress : 100}
        color="info"
        sx={{
          height: '3px',
          bgcolor: 'transparent',
          visibility: isPending ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
};
